import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { request, BaseResponse } from '@/lib/api';
import { User } from '@/lib/api/user';

// 权限接口定义
export interface Permission {
  id: string;
  name: string;
  code: string;
  description?: string;
  resource?: string;
  action?: string;
}

// 企业信息接口
export interface CorpInfo {
  id: string;
  name: string;
  code?: string;
  description?: string;
  logo?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
}

// 环境变量配置接口
export interface EnvConfig {
  api_base_url?: string;
  app_name?: string;
  app_version?: string;
  features?: Record<string, boolean>;
  limits?: Record<string, number>;
  [key: string]: any;
}

// 用户权限响应接口
export interface UserPermissionsResponse {
  permissionData: {
    permissions: Permission[];
    roles: Array<{
      id: string;
      name: string;
      code: string;
      permissions: Permission[];
    }>;
    corp_info?: CorpInfo;
    env_config?: EnvConfig;
  };
  settingData?: Record<string, any>;
}

// Store状态接口
export interface UserStoreState {
  // 用户信息
  user: User | null;
  
  // 权限信息
  permissions: Permission[];
  roles: Array<{
    id: string;
    name: string;
    code: string;
    permissions: Permission[];
  }>;
  
  // 企业信息
  corpInfo: CorpInfo | null;
  
  // 环境配置
  envConfig: EnvConfig | null;
  
  // 设置数据
  settingData: Record<string, any> | null;
  
  // 加载状态
  loading: boolean;
  initialized: boolean;
  
  // Actions
  setUser: (user: User | null) => void;
  setPermissions: (data: UserPermissionsResponse) => void;
  fetchUserPermissions: () => Promise<void>;
  clearUserData: () => void;
  hasPermission: (permissionCode: string) => boolean;
  hasRole: (roleCode: string) => boolean;
  initialize: () => Promise<void>;
}

// 获取用户权限的API函数
export async function getUserPermissions(): Promise<BaseResponse<UserPermissionsResponse>> {
  return request<UserPermissionsResponse>('/permissions/my-permissions');
}

// 创建用户Store
export const useUserStore = create<UserStoreState>()
  (persist(
    (set, get) => ({
      // 初始状态
      user: null,
      permissions: [],
      roles: [],
      corpInfo: null,
      envConfig: null,
      settingData: null,
      loading: false,
      initialized: false,

      // 设置用户信息
      setUser: (user) => {
        set({ user });
      },

      // 设置权限信息
      setPermissions: (data) => {
        
        const permissionData = data.permissionData || {};
        const settingData = data.settingData || {};
        console.log('settingData==============', settingData);
        
        set({
          permissions: permissionData.permissions || [],
          roles: permissionData.roles || [],
          corpInfo: permissionData.corp_info || null,
          envConfig: permissionData.env_config || null,
          settingData: settingData,
        });
      },

      // 获取用户权限
      fetchUserPermissions: async () => {
        console.log('fetchUserPermissions: 开始执行');
        const { loading } = get();
        console.log('fetchUserPermissions: loading状态:', loading);
        if (loading) {
          console.log('fetchUserPermissions: 正在加载中，跳过');
          return;
        }

        set({ loading: true });
        try {
          console.log('fetchUserPermissions: 开始调用API');
          const response = await getUserPermissions();
          console.log('fetchUserPermissions: API响应:', response);
          if (response.success && response.data) {
            console.log('fetchUserPermissions: 设置权限数据');
            get().setPermissions(response.data);
          } else {
            console.error('获取用户权限失败:', response.errorMessage);
          }
        } catch (error) {
          console.error('获取用户权限异常:', error);
        } finally {
          set({ loading: false });
        }
      },

      // 清除用户数据
      clearUserData: () => {
        set({
          user: null,
          permissions: [],
          roles: [],
          corpInfo: null,
          envConfig: null,
          settingData: null,
          initialized: false,
        });
      },

      // 检查是否有指定权限
      hasPermission: (permissionCode) => {
        const { permissions } = get();
        return permissions.some(permission => permission.code === permissionCode);
      },

      // 检查是否有指定角色
      hasRole: (roleCode) => {
        const { roles } = get();
        return roles.some(role => role.code === roleCode);
      },

      // 初始化用户数据
      initialize: async () => {
        console.log('initialize: 开始执行');
        const { initialized, loading } = get();
        console.log('initialize: initialized:', initialized, 'loading:', loading);
        
        // 如果正在加载或已初始化，跳过
        if (loading || initialized) {
          console.log('initialize: 正在加载或已初始化，跳过');
          return;
        }

        set({ loading: true });
        try {
          console.log('initialize: 开始调用API获取用户权限信息');
          const response = await getUserPermissions();
          console.log('initialize: API响应:', response);
          if (response.success && response.data) {
            console.log('initialize: 设置权限数据');
            get().setPermissions(response.data);
            console.log('initialize: 权限信息获取完成，设置initialized为true');
            set({ initialized: true });
          } else {
            console.error('初始化获取用户权限失败:', response.errorMessage);
          }
        } catch (error) {
          console.error('初始化用户数据失败:', error);
        } finally {
          set({ loading: false });
        }
      },
    }),
    {
      name: 'user-store', // 存储的key名称
      storage: createJSONStorage(() => localStorage), // 使用localStorage进行持久化
      partialize: (state) => ({
        // 只持久化这些字段，不持久化loading等临时状态
        user: state.user,
        permissions: state.permissions,
        roles: state.roles,
        corpInfo: state.corpInfo,
        envConfig: state.envConfig,
        settingData: state.settingData,
        initialized: state.initialized,
      }),
    }
  ));

// 导出一些便捷的选择器
export const useUser = () => useUserStore(state => state.user);
export const usePermissions = () => useUserStore(state => state.permissions);
export const useRoles = () => useUserStore(state => state.roles);
export const useCorpInfo = () => useUserStore(state => state.corpInfo);
export const useEnvConfig = () => useUserStore(state => state.envConfig);
export const useSettingData = () => useUserStore(state => state.settingData);
export const useUserLoading = () => useUserStore(state => state.loading);
export const useUserInitialized = () => useUserStore(state => state.initialized);

// 权限检查hooks
export const useHasPermission = (permissionCode: string) => {
  return useUserStore(state => state.hasPermission(permissionCode));
};

export const useHasRole = (roleCode: string) => {
  return useUserStore(state => state.hasRole(roleCode));
};